import React, { useRef, useState, useEffect } from "react";
import "./PayoutDateComponent.css";
import { FiCalendar, FiDownload, FiUpload, FiX, FiCheckCircle, FiAlertCircle, FiArrowLeft } from 'react-icons/fi';
import { BsCloudUpload, BsFileEarmarkExcel } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';

// Success Popup Component with unique classnames
const SuccessPopup = ({ onClose }) => (
  <div className="payout-success-popup-overlay" onClick={onClose}>
    <div className="payout-success-popup-container" onClick={(e) => e.stopPropagation()}>
      <div className="payout-success-popup-header">
        <FiCheckCircle className="payout-success-popup-icon" />
      </div>
      <h3 className="payout-success-popup-title">Successful</h3>
      <div className="payout-success-popup-body">
        <p className="payout-success-popup-message">File uploaded successfully!</p>
      </div>
      <div className="payout-success-popup-footer">
        <button className="payout-success-popup-ok-btn" onClick={onClose}>
          OK
        </button>
      </div>
    </div>
  </div>
);

// Calendar Component with unique classnames
const CustomCalendar = ({ onDateSelect, onClose, currentDate }) => {
  const [viewDate, setViewDate] = useState(currentDate || new Date());
  const [selectedDate, setSelectedDate] = useState(currentDate || null);
  
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  
  const daysInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1).getDay();
  
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  
  const handlePrevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };
  
  const handleNextMonth = () => {
    const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);
    if (newDate.getFullYear() < currentYear || 
        (newDate.getFullYear() === currentYear && newDate.getMonth() <= currentMonth)) {
      setViewDate(newDate);
    }
  };
  
  const handleDateClick = (day) => {
    const clickedDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    setSelectedDate(clickedDate);
    onDateSelect(clickedDate);
    onClose();
  };
  
  const isToday = (day) => {
    return day === today.getDate() && 
           viewDate.getMonth() === today.getMonth() && 
           viewDate.getFullYear() === today.getFullYear();
  };
  
  const isSelected = (day) => {
    return selectedDate && 
           day === selectedDate.getDate() && 
           viewDate.getMonth() === selectedDate.getMonth() && 
           viewDate.getFullYear() === selectedDate.getFullYear();
  };
  
  const isFutureMonth = () => {
    return viewDate.getFullYear() > currentYear || 
           (viewDate.getFullYear() === currentYear && viewDate.getMonth() > currentMonth);
  };
  
  const renderCalendar = () => {
    const blanks = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      blanks.push(
        <td 
          key={`calendar-blank-${i}`} 
          className="calendar-cell calendar-cell-blank"
        ></td>
      );
    }
    
    const days = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const isTodayClass = isToday(d) ? 'calendar-cell-today' : '';
      const isSelectedClass = isSelected(d) ? 'calendar-cell-selected' : '';
      
      days.push(
        <td 
          key={`calendar-day-${d}`} 
          className={`calendar-cell ${isTodayClass} ${isSelectedClass}`}
          onClick={() => handleDateClick(d)}
        >
          <span className="calendar-day-number">{d}</span>
        </td>
      );
    }
    
    const totalSlots = [...blanks, ...days];
    const rows = [];
    let cells = [];
    
    totalSlots.forEach((cell, i) => {
      if (i % 7 !== 0) {
        cells.push(cell);
      } else {
        if (i !== 0) {
          rows.push(
            <tr key={`calendar-row-${rows.length}`} className="calendar-row">
              {cells}
            </tr>
          );
        }
        cells = [cell];
      }
      if (i === totalSlots.length - 1) {
        rows.push(
          <tr key={`calendar-row-${rows.length}`} className="calendar-row">
            {cells}
          </tr>
        );
      }
    });
    
    return rows;
  };
  
  return (
    <div className="custom-calendar-container">
      <div className="calendar-header-section">
        <button 
          className="calendar-nav-btn calendar-prev-btn" 
          onClick={handlePrevMonth}
        >
          ←
        </button>
        <span className="calendar-month-year-display">
          {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
        </span>
        <button 
          className={`calendar-nav-btn calendar-next-btn ${isFutureMonth() ? 'calendar-nav-disabled' : ''}`} 
          onClick={handleNextMonth}
          disabled={isFutureMonth()}
        >
          →
        </button>
      </div>
      <table className="calendar-grid-table">
        <thead className="calendar-grid-header">
          <tr className="calendar-header-row">
            <th className="calendar-weekday-header">Sun</th>
            <th className="calendar-weekday-header">Mon</th>
            <th className="calendar-weekday-header">Tue</th>
            <th className="calendar-weekday-header">Wed</th>
            <th className="calendar-weekday-header">Thu</th>
            <th className="calendar-weekday-header">Fri</th>
            <th className="calendar-weekday-header">Sat</th>
          </tr>
        </thead>
        <tbody className="calendar-grid-body">
          {renderCalendar()}
        </tbody>
      </table>
    </div>
  );
};

const PayoutDateComponent = ({
  payoutDate,
  selectedFile,
  onDateChange,
  onFileChange,
  onDownloadSample,
  onUpload,
  onCancel,
  onDragOver,
  onDrop,
  isFormValid,
  uploading,
  error,
  success,
  dateMismatch,
  showSuccessPopup,
  onSuccessPopupClose,
  csvHeaders
}) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const calendarRef = useRef(null);
  const [showCalendar, setShowCalendar] = useState(false);

  const handleFileInputClick = () => fileInputRef.current.click();

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onFileChange(file);
  };

  const handleBack = () => navigate(-1);

  const removeFile = () => {
    onFileChange(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCalendar]);

  const handleDateSelect = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    onDateChange(`${month}/${day}/${year}`);
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const [month, day, year] = dateString.split('/');
    return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
  };

  const getCurrentDate = () => {
    if (!payoutDate) return new Date();
    const [month, day, year] = payoutDate.split('/');
    return new Date(year, month - 1, day);
  };

  const getFileDisplayText = () => {
    if (!selectedFile) return null;
    
    if (csvHeaders && csvHeaders.length > 0) {
      const headerCount = csvHeaders.length;
      const headerPreview = csvHeaders.slice(0, 3).join(', ') + (csvHeaders.length > 3 ? '...' : '');
      
      return (
        <div className="file-details-container">
          <span className="file-name-text">{selectedFile.name}</span>
          <span className="file-meta-info">
            {(selectedFile.size / 1024).toFixed(2)} KB • {headerCount} columns • Click to change
          </span>
          <span className="file-headers-preview">
            Headers: {headerPreview}
          </span>
        </div>
      );
    }
    
    return (
      <div className="file-details-container">
        <span className="file-name-text">{selectedFile.name}</span>
        <span className="file-meta-info">
          {(selectedFile.size / 1024).toFixed(2)} KB • Click to change
        </span>
      </div>
    );
  };

  return (
    <div className="payout-page-main-wrapper">
      {/* Header Section */}
      <div className="payout-page-header-section">
        <button className="payout-back-navigation-btn" onClick={handleBack}>
          <FiArrowLeft className="payout-back-icon" />
        </button>
        <h2 className="payout-page-title">Settlement Date</h2>
      </div>
      
      <div className="payout-main-card">
        <div className="payout-card-inner-body">
          {/* Date Selection Section */}
          <div className="payout-date-selection-section">
            <div className="payout-section-header-container">
              <h3 className="payout-section-title-text">Select a Settlement Date</h3>
            </div>
            
            <div className="payout-date-picker-container">
              <div className="payout-date-input-wrapper">
                <div className="payout-input-label">Date</div>
                <div className="payout-input-group">
                  <input
                    type="text"
                    className={`payout-date-input-field ${dateMismatch ? 'payout-input-error' : ''}`}
                    value={formatDisplayDate(payoutDate)}
                    onClick={() => setShowCalendar(true)}
                    placeholder="DD-MM-YYYY"
                    readOnly
                  />
                  <button 
                    className="payout-calendar-toggle-button" 
                    onClick={() => setShowCalendar(true)} 
                    type="button"
                  >
                    <FiCalendar />
                  </button>
                </div>
                
                {showCalendar && (
                  <div className="payout-calendar-popup-container" ref={calendarRef}>
                    <CustomCalendar 
                      onDateSelect={handleDateSelect}
                      onClose={() => setShowCalendar(false)}
                      currentDate={getCurrentDate()}
                    />
                  </div>
                )}
              </div>
              
              <button className="payout-download-sample-button" onClick={onDownloadSample}>
                <FiDownload className="payout-button-icon" />
                Download Sample Format
              </button>
            </div>
          </div>

          {/* Upload Section */}
          <div className="payout-upload-section">
            <div className="payout-section-header-container">
              <h3 className="payout-section-title-text">Upload File</h3>
            </div>
            
            <div 
              className={`payout-file-dropzone ${selectedFile ? 'payout-file-selected' : ''} ${dateMismatch ? 'payout-dropzone-error' : ''}`}
              onClick={handleFileInputClick}
              onDragOver={onDragOver}
              onDrop={onDrop}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileInputChange}
                accept=".csv,text/csv"
                className="payout-file-input-hidden"
              />
              
              {selectedFile ? (
                <div className="payout-selected-file-display">
                  <div className="payout-file-type-icon">
                    <BsFileEarmarkExcel />
                  </div>
                  {getFileDisplayText()}
                  <button 
                    className="payout-remove-file-button" 
                    onClick={(e) => { e.stopPropagation(); removeFile(); }}
                  >
                    <FiX />
                  </button>
                </div>
              ) : (
                <div className="payout-upload-placeholder-content">
                  <div className="payout-upload-icon-circle">
                    <BsCloudUpload className="payout-upload-icon" />
                  </div>
                  <p className="payout-upload-instruction">
                    <span className="payout-primary-text">Choose file or drag here</span>
                  </p>
                  <p className="payout-upload-format-hint">Supported format: CSV upto 5MB</p>
                </div>
              )}
            </div>
            
            {/* Status Messages */}
            {error && (
              <div className="payout-status-message payout-error-message">
                <FiAlertCircle className="payout-status-icon" />
                <span>{error}</span>
              </div>
            )}
            
            {success && (
              <div className="payout-status-message payout-success-message">
                <FiCheckCircle className="payout-status-icon" />
                <span>{success}</span>
              </div>
            )}
            
            {/* Action Buttons */}
            {isFormValid && (
              <div className="payout-action-buttons-row">
                <button 
                  className="payout-action-btn payout-upload-action-btn" 
                  onClick={onUpload} 
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <span className="payout-spinner-indicator"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <FiUpload className="payout-btn-icon" />
                      Upload File
                    </>
                  )}
                </button>
                <button 
                  className="payout-action-btn payout-cancel-action-btn" 
                  onClick={onCancel} 
                  disabled={uploading}
                >
                  <FiX className="payout-btn-icon" />
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
   
      {showSuccessPopup && <SuccessPopup onClose={onSuccessPopupClose} />}
    </div>
  );
};

export default PayoutDateComponent;